import { ChevronLeft, ChevronRight, FileText, Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { useBookingRequests } from '@/hooks/useBookingRequests';
import { useExperiences } from '@/hooks/useExperiences';
import { useInvoicesForPeriod } from '@/hooks/useInvoices';
import { usePartners } from '@/hooks/usePartners';
import { resolveBookingCommission, type BookingRequestType } from '@/types/types_bookingRequest';
import {
  buildInvoiceLines,
  formatCommissionDeduction,
  invoiceDocId,
  invoicePeriodLabel,
  invoiceStatusLabels,
  invoiceTotals,
  lastCompletePeriod,
  shiftPeriod,
  type InvoiceStatusType,
  type InvoiceType,
} from '@/types/types_invoice';
import { callableErrorMessage, generateInvoiceDraft } from '@/util/callable_functions';
import { formatJpy } from '@/util/util_format';

const statusPillClass: Record<InvoiceStatusType, string> = {
  draft: 'tw:bg-amber-100 tw:text-amber-900',
  issued: 'tw:bg-green-100 tw:text-green-800',
  void: 'tw:bg-gray-100 tw:text-gray-600',
};

/** What one partner's row is showing: a raised invoice, or what one would come to. */
interface RowType {
  partnerId: string;
  partnerName: string;
  invoice?: InvoiceType;
  lineCount: number;
  subtotalGross: number;
  totalCommission: number;
  total: number;
}

/**
 * A month of invoicing, one row per partner.
 *
 * The screen is a month at a time because that is the unit of work: an admin
 * settles June for every partner, then moves on. It opens on the last month that
 * has finished — see `lastCompletePeriod` — rather than the one in progress,
 * which is incomplete by definition.
 *
 * A partner with no invoice yet still shows figures. They are computed in the
 * browser by the same `buildInvoiceLines` the Cloud Function uses, so the
 * preview and the draft cannot disagree — which is what makes Generate a
 * decision rather than a guess.
 */
export function PageInvoiceList() {
  const [period, setPeriod] = useState(lastCompletePeriod);
  const { partners, loading: partnersLoading } = usePartners();
  const { requests, loading: requestsLoading } = useBookingRequests();
  const { experiences, loading: experiencesLoading } = useExperiences();
  const { invoices, loading: invoicesLoading } = useInvoicesForPeriod(period);

  const [generating, setGenerating] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Experiences count towards loading even though no column shows one. They are
  // where the commission rate comes from for any booking confirmed before that
  // rate started being frozen onto the booking itself, so a table drawn before
  // they arrive states a commission of zero and then corrects itself — which
  // reads exactly like the bug of a rate that was never picked up.
  const loading = partnersLoading || requestsLoading || invoicesLoading || experiencesLoading;

  const rows = useMemo<RowType[]>(() => {
    // Read off the live experience only for a booking confirmed before its rate
    // was frozen — which is what `resolveBookingCommission` decides. The Cloud
    // Function does exactly the same from its own read.
    const commissionOf = (booking: BookingRequestType) =>
      resolveBookingCommission(booking, experiences.find((e) => e.id === booking.experienceId) ?? null);

    return partners
      .map((partner) => {
        const invoice = invoices.get(partner.id);
        if (invoice) {
          return {
            partnerId: partner.id,
            partnerName: partner.name,
            invoice,
            lineCount: invoice.lines.length,
            subtotalGross: invoice.subtotalGross,
            totalCommission: invoice.totalCommission,
            total: invoice.total,
          };
        }

        const lines = buildInvoiceLines(
          requests.filter((r) => r.partnerId === partner.id),
          period,
          commissionOf,
        );
        return { partnerId: partner.id, partnerName: partner.name, lineCount: lines.length, ...invoiceTotals(lines) };
      })
      // Partners with something to bill first; the rest still listed, because
      // "nothing this month" is an answer somebody came here for.
      .sort((a, b) => Number(!!b.lineCount) - Number(!!a.lineCount) || a.partnerName.localeCompare(b.partnerName));
  }, [partners, requests, experiences, invoices, period]);

  async function handleGenerate(partnerId: string) {
    setGenerating(partnerId);
    setError(null);
    try {
      await generateInvoiceDraft({ partnerId, period });
      // The subscription puts the draft in the row.
    } catch (e) {
      console.error('generateInvoiceDraft failed', e);
      setError(callableErrorMessage(e));
    } finally {
      setGenerating(null);
    }
  }

  const billable = rows.filter((row) => row.lineCount > 0);

  return (
    <div>
      <header className='tw:mb-6 tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-4'>
        <div>
          <h1 className='tw:font-serif tw:text-2xl tw:text-ed-black'>Invoices</h1>
          <p className='tw:mt-1 tw:text-sm tw:text-ed-stone'>
            One invoice per partner per month, raised from the experiences delivered that month.{' '}
            {billable.length} of {rows.length} partners have something to bill.
          </p>
        </div>

        <div className='tw:flex tw:items-center tw:gap-2'>
          <Button variant='outline' size='icon' onClick={() => setPeriod(shiftPeriod(period, -1))} aria-label='Previous month'>
            <ChevronLeft className='tw:size-4' />
          </Button>
          <span className='tw:min-w-36 tw:text-center tw:font-medium'>{invoicePeriodLabel(period)}</span>
          <Button variant='outline' size='icon' onClick={() => setPeriod(shiftPeriod(period, 1))} aria-label='Next month'>
            <ChevronRight className='tw:size-4' />
          </Button>
        </div>
      </header>

      {error && (
        <p className='tw:mb-4 tw:rounded-md tw:border tw:border-ed-red/40 tw:bg-ed-red/5 tw:px-4 tw:py-3 tw:text-sm tw:text-ed-red'>
          {error}
        </p>
      )}

      <div className='tw:overflow-x-auto tw:rounded-sm tw:border tw:border-ed-umber/10 tw:bg-white tw:shadow-sm'>
        <table className='tw:w-full'>
          <thead className='tw:bg-ed-cream/50'>
            <tr className='tw:text-left tw:text-xs tw:tracking-wider tw:uppercase tw:text-ed-stone'>
              <th className='tw:px-4 tw:py-3'>Partner</th>
              <th className='tw:px-4 tw:py-3 tw:text-right'>Lines</th>
              <th className='tw:px-4 tw:py-3 tw:text-right'>Gross</th>
              <th className='tw:px-4 tw:py-3 tw:text-right'>Commission</th>
              <th className='tw:px-4 tw:py-3 tw:text-right'>Total</th>
              <th className='tw:px-4 tw:py-3'>Status</th>
              <th className='tw:px-4 tw:py-3'></th>
            </tr>
          </thead>
          <tbody className='tw:divide-y tw:divide-ed-umber/10 tw:text-sm'>
            {loading && (
              <tr>
                <td colSpan={7} className='tw:px-4 tw:py-8 tw:text-center tw:text-ed-stone'>Loading…</td>
              </tr>
            )}

            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={7} className='tw:px-4 tw:py-8 tw:text-center tw:text-ed-stone'>
                  No active partners.
                </td>
              </tr>
            )}

            {!loading && rows.map((row) => (
              <tr key={row.partnerId} className={row.lineCount ? '' : 'tw:text-ed-stone'}>
                <td className='tw:px-4 tw:py-3 tw:font-medium'>{row.partnerName}</td>
                <td className='tw:px-4 tw:py-3 tw:text-right tw:tabular-nums'>{row.lineCount || '—'}</td>
                <td className='tw:px-4 tw:py-3 tw:text-right tw:tabular-nums'>
                  {row.lineCount ? formatJpy(row.subtotalGross) : '—'}
                </td>
                <td className='tw:px-4 tw:py-3 tw:text-right tw:tabular-nums'>
                  {formatCommissionDeduction(row.totalCommission, formatJpy)}
                </td>
                <td className='tw:px-4 tw:py-3 tw:text-right tw:font-medium tw:tabular-nums'>
                  {row.lineCount ? formatJpy(row.total) : '—'}
                </td>
                <td className='tw:px-4 tw:py-3'>
                  {row.invoice ? (
                    <span className={`tw:rounded-full tw:px-2 tw:py-0.5 tw:text-xs tw:font-medium ${statusPillClass[row.invoice.status]}`}>
                      {invoiceStatusLabels[row.invoice.status]}
                    </span>
                  ) : (
                    <span className='tw:text-xs tw:text-ed-stone'>Not raised</span>
                  )}
                </td>
                <td className='tw:px-4 tw:py-3 tw:text-right'>
                  {row.invoice ? (
                    <Link
                      to={`/admin/invoices/${invoiceDocId(row.partnerId, period)}`}
                      className='tw:inline-flex tw:items-center tw:gap-1.5 tw:text-sm tw:font-medium tw:text-ed-black tw:underline-offset-4 tw:hover:underline'
                    >
                      <FileText className='tw:size-4' /> Open
                    </Link>
                  ) : (
                    <Button
                      variant='outline'
                      size='sm'
                      // Nothing to bill is not a draft worth raising: an invoice
                      // with no lines cannot be issued anyway.
                      disabled={!row.lineCount || generating === row.partnerId}
                      onClick={() => void handleGenerate(row.partnerId)}
                    >
                      {generating === row.partnerId && <Loader2 className='tw:size-3.5 tw:animate-spin' />}
                      Generate draft
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
